import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import {
  WIDGET_GROUPS,
  type WidgetId,
  saveEnabledWidgets,
  widgetsByGroup,
} from '@/lib/dashboard';

interface Props {
  initialEnabled: WidgetId[];
  onSave: (next: WidgetId[]) => void;
  onClose: () => void;
}

export function DashboardConfig({ initialEnabled, onSave, onClose }: Props) {
  const [selected, setSelected] = useState<Set<WidgetId>>(
    () => new Set(initialEnabled),
  );
  const groups = widgetsByGroup();

  const toggle = (id: WidgetId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = () => {
    const arr = Array.from(selected);
    saveEnabledWidgets(arr);
    onSave(arr);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-stone-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-stone-200 dark:border-stone-800 px-5 py-3">
          <h2 className="text-lg font-semibold">Configure dashboard</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <p className="text-sm text-stone-600 dark:text-stone-400">
            Pick the widgets you want on your home page. Saved per
            browser — sign-in from a different machine carries the
            defaults until you configure there too.
          </p>

          {WIDGET_GROUPS.map((g) => (
            <section key={g}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
                {g}
              </h3>
              <div className="grid gap-2 md:grid-cols-2">
                {groups[g].map((w) => {
                  const checked = selected.has(w.id);
                  return (
                    <label
                      key={w.id}
                      className="flex items-start gap-2 rounded-md border border-stone-200 dark:border-stone-800 p-2 hover:bg-stone-50 dark:hover:bg-stone-800/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(w.id)}
                        className="mt-0.5 h-4 w-4"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">
                          {w.title}
                        </span>
                        <span className="block text-xs text-stone-500 dark:text-stone-400">
                          {w.description}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stone-200 dark:border-stone-800 px-5 py-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save}>Save selection</Button>
        </div>
      </div>
    </div>
  );
}
